import { useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import Avatar from '@/components/base/Avatar';
import { formatMoney } from '@/utils/format';
import { channelMeta } from '@/utils/payment';
import type {
  FundingWallet,
  PayeeInvoice,
  PayoutRail,
  SinglePayee,
  SinglePayment,
  SinglePaymentChannel,
} from '@/types/portal';

interface PaymentConfirmModalProps {
  open: boolean;
  onClose: () => void;
  payee: SinglePayee | null;
  channel: SinglePaymentChannel;
  invoice: PayeeInvoice | null;
  targetLabel: string;
  rail: PayoutRail | null;
  wallet: FundingWallet | null;
  currency: string;
  amount: number;
  fee: number;
  totalDebit: number;
  reference: string;
  canApprove: boolean;
  sent: SinglePayment | null;
  /** A brand-new payee that was just paid and is not yet in the directory. */
  newPayee: SinglePayee | null;
  /** Whether that new payee has been saved to the directory. */
  payeeSaved: boolean;
  onSavePayee: () => void;
  onConfirm: () => void;
}

export default function PaymentConfirmModal({
  open,
  onClose,
  payee,
  channel,
  invoice,
  targetLabel,
  rail,
  wallet,
  currency,
  amount,
  fee,
  totalDebit,
  reference,
  canApprove,
  sent,
  newPayee,
  payeeSaved,
  onSavePayee,
  onConfirm,
}: PaymentConfirmModalProps) {
  const [note, setNote] = useState('');

  if (sent) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        icon="ri-checkbox-circle-line"
        title="Payment submitted"
        subtitle={`${sent.reference} is ${sent.status.toLowerCase()}.`}
        footer={
          <Button icon="ri-exchange-dollar-line" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col items-center py-2 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
            <i className="ri-check-line" />
          </span>
          <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">
            {formatMoney(sent.amount, sent.currency)}
          </p>
          <div className="mt-2">
            <StatusBadge status={sent.status} />
          </div>
          <div className="mt-5 w-full space-y-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-left text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground-500">Payee</span>
              <span className="truncate text-foreground-900">{sent.payeeName}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground-500">Destination</span>
              <span className="truncate text-foreground-900">{sent.target}</span>
            </div>
            {sent.campaign ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-foreground-500">Campaign</span>
                <span className="truncate text-foreground-900">{sent.campaign}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground-500">Reference</span>
              <span className="text-foreground-900">{sent.reference}</span>
            </div>
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            {sent.status === 'Scheduled' || sent.status === 'Paid'
              ? 'Funds are on the way. Track settlement progress in Transactions.'
              : 'The payment is queued for Finance approval. Funds release once approved.'}
          </p>
          {newPayee ? (
            payeeSaved ? (
              <div className="mt-5 flex w-full items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-left">
                <i className="ri-bookmark-line text-accent-700" />
                <p className="text-xs text-accent-800">
                  <span className="font-medium">{newPayee.name}</span> saved to your payee
                  directory — they’ll show up in search next time.
                </p>
              </div>
            ) : (
              <div className="mt-5 w-full rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-left">
                <div className="flex items-start gap-2">
                  <i className="mt-0.5 ri-bookmark-line text-secondary-700" />
                  <div>
                    <p className="text-sm font-medium text-secondary-900">
                      Save {newPayee.name} to your directory?
                    </p>
                    <p className="mt-0.5 text-xs text-secondary-800">
                      Add them as a saved payee so you can pay again without re-entering the
                      destination.
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="ri-bookmark-line"
                  className="mt-3"
                  onClick={onSavePayee}
                >
                  Save this payee
                </Button>
              </div>
            )
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        setNote('');
        onClose();
      }}
      icon="ri-send-plane-line"
      title={`Send payment ${reference}`}
      subtitle="Review the settlement before it leaves the portal."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              setNote('');
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant={canApprove ? 'primary' : 'accent'}
            icon={canApprove ? 'ri-send-plane-line' : 'ri-check-double-line'}
            onClick={() => {
              onConfirm();
              setNote('');
            }}
          >
            {canApprove ? 'Confirm & send' : 'Submit for approval'}
          </Button>
        </>
      }
    >
      {payee ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-3 py-3">
            <Avatar src={payee.avatar} alt={payee.name} size="md" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground-950">{payee.name}</p>
              <p className="truncate text-xs text-foreground-500">
                {channelMeta[channel].label} · {targetLabel}
              </p>
            </div>
            <span className="shrink-0 text-sm font-medium text-foreground-950">
              {formatMoney(amount, currency)}
            </span>
          </div>

          <dl className="space-y-2 rounded-md border border-background-200 px-4 py-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-foreground-500">Destination</dt>
              <dd className="truncate text-foreground-900">{targetLabel}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-foreground-500">Wallet</dt>
              <dd className="truncate text-foreground-900">{wallet?.rail ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-foreground-500">Payout rail</dt>
              <dd className="text-foreground-900">{rail?.name ?? '—'}</dd>
            </div>
            {invoice ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500">Campaign</dt>
                  <dd className="truncate text-foreground-900">{invoice.campaign}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">Invoice {invoice.number}</dt>
                  <dd className="text-foreground-800">{formatMoney(invoice.amount, currency)}</dd>
                </div>
                {invoice.advance ? (
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">Less early payout advance</dt>
                    <dd className="text-secondary-800">
                      − {formatMoney(invoice.advance.amount, currency)}
                    </dd>
                  </div>
                ) : null}
              </>
            ) : null}
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Amount</dt>
              <dd className="text-foreground-900">{formatMoney(amount, currency)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-foreground-500">Transfer fee</dt>
              <dd className="text-foreground-900">
                {fee > 0 ? formatMoney(fee, currency) : 'No fee'}
              </dd>
            </div>
            <div className="flex items-center justify-between border-t border-background-100 pt-2">
              <dt className="font-medium text-foreground-800">Total debit</dt>
              <dd className="font-heading text-base font-semibold text-foreground-950">
                {formatMoney(totalDebit, currency)}
              </dd>
            </div>
          </dl>

          <div>
            <label
              htmlFor="send-payment-note"
              className="text-xs font-label font-medium text-foreground-600"
            >
              Approval note (optional)
            </label>
            <textarea
              id="send-payment-note"
              value={note}
              maxLength={500}
              rows={3}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add context for the audit trail..."
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <p className="mt-1 text-right text-[11px] text-foreground-400">{note.length}/500</p>
          </div>

          <div
            className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
              canApprove
                ? 'border-accent-200 bg-accent-50 text-accent-800'
                : 'border-secondary-200 bg-secondary-50 text-secondary-900'
            }`}
          >
            <i className={`mt-0.5 ${canApprove ? 'ri-shield-check-line' : 'ri-hourglass-line'}`} />
            <span>
              {canApprove
                ? 'As an approver, confirming debits the wallet and settles on the selected rail immediately.'
                : 'Your confirmation submits this payment for Finance approval. No funds move until an approver signs off.'}
            </span>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}